import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { RadioIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface MediaOutlet {
  name: string;
  type: string;
  url?: string;
  emoji: string;
}
const mediaOutlets: MediaOutlet[] = [{
  name: 'La Prensa Honduras',
  type: 'Newspaper',
  url: 'https://www.laprensa.hn/',
  emoji: '📰'
}, {
  name: 'El Heraldo',
  type: 'Newspaper',
  url: 'https://www.elheraldo.hn/',
  emoji: '📰'
}, {
  name: 'La Tribuna',
  type: 'Newspaper',
  url: 'https://www.latribuna.hn/',
  emoji: '📰'
}, {
  name: 'Proceso Digital',
  type: 'Digital News',
  url: 'https://www.proceso.hn',
  emoji: '💻'
}, {
  name: 'Confidencial Honduras',
  type: 'Investigative',
  url: 'https://confidencialhn.com',
  emoji: '🔍'
}, {
  name: 'HRN Radio',
  type: 'Radio',
  url: 'https://www.radiohrn.hn',
  emoji: '📻'
}, {
  name: 'Televicentro',
  type: 'Television',
  url: 'https://www.televicentro.com/',
  emoji: '📺'
}, {
  name: 'Radio América',
  type: 'Radio',
  url: 'https://radioamerica.hn',
  emoji: '📻'
}, {
  name: 'El Pulso Honduras',
  type: 'Digital News',
  url: 'https://elpulso.hn',
  emoji: '💻'
}, {
  name: 'Criterio.hn',
  type: 'Digital News',
  url: 'https://criterio.hn',
  emoji: '💻'
}, {
  name: 'Tiempo Digital',
  type: 'Newspaper',
  url: 'https://www.tiempo.hn',
  emoji: '📰'
}];
function MediaCard({
  outlet
}: {
  outlet: MediaOutlet;
}) {
  const {
    t
  } = useLanguage();
  const typeKeyMap: Record<string, string> = {
    Newspaper: 'media.type.newspaper',
    'Digital News': 'media.type.digitalNews',
    Investigative: 'media.type.investigative',
    Radio: 'media.type.radio',
    Television: 'media.type.television'
  };
  const translatedType = typeKeyMap[outlet.type] ? t(typeKeyMap[outlet.type]) : outlet.type;
  return <div className="flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-2xl border border-white/10 bg-white/5 mx-3 group cursor-default select-none" style={{
    minWidth: '220px'
  }} data-id="element-1911">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 text-xl" data-id="element-1912">
        {outlet.emoji}
      </div>
      <div className="flex flex-col min-w-0" data-id="element-1913">
        <span className="font-bebas text-base tracking-wider leading-tight text-white" data-id="element-1914">
          {outlet.name}
        </span>
        <span className="font-nunito text-xs text-white/50 leading-tight truncate" data-id="element-1915">
          {translatedType}
        </span>
      </div>
      {outlet.url && <a href={outlet.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" aria-label={`Visit ${outlet.name}`} data-id="element-1916">
          <ExternalLinkIcon className="w-3.5 h-3.5 text-white/40 hover:text-haa-skyblue transition-colors" data-id="element-1917" />
        </a>}
    </div>;
}
export function MediaScroller() {
  const titleRef = useRef<HTMLDivElement>(null);
  const inView = useInView(titleRef, {
    once: true
  });
  const {
    t
  } = useLanguage();
  const doubled = [...mediaOutlets, ...mediaOutlets];
  return <section className="py-10 overflow-hidden bg-[#1565C0]" aria-label="Honduras media outlets" data-id="element-1918">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" data-id="element-1919">
        <motion.div ref={titleRef} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="flex items-center gap-3" data-id="element-1920">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-haa-skyblue to-haa-midblue flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-1921">
            <RadioIcon className="w-4 h-4 text-white" data-id="element-1922" />
          </div>
          <div data-id="element-1923">
            <span className="font-nunito text-haa-skyblue text-xs uppercase tracking-[0.3em] font-semibold block leading-none mb-0.5" data-id="element-1924">
              {t('media.label')}
            </span>
            <h2 className="font-bebas text-2xl text-white tracking-wider leading-none" data-id="element-1925">
              {t('media.heading')}
            </h2>
          </div>
        </motion.div>
      </div>

      {/* Scroller track */}
      <div className="relative w-full" style={{
      maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
    }} data-id="element-1926">
        <div className="flex nonprofit-scroll" style={{
        width: 'max-content'
      }} onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'paused';
      }} onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'running';
      }} data-id="element-1927">
          {doubled.map((outlet, i) => <MediaCard key={`${outlet.name}-${i}`} outlet={outlet} data-id="element-1928" />)}
        </div>
      </div>
    </section>;
}